import { FC } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Skeleton,
} from "@mui/material";
import { Add as AddIcon, People as PeopleIcon } from "@mui/icons-material";
import MainTitle from "../../components/mainTitle";
import { TRole } from "../../../apis/rbac/rbac.interfaces";

interface RoleListViewProps {
  roles: TRole[];
  selectedRoleId: string | null;
  onSelectRole: (id: string) => void;
  onAddRole: () => void;
  isLoading: boolean;
  roleUserCounts?: Record<string, number>;
}

const RoleListView: FC<RoleListViewProps> = ({
  roles,
  selectedRoleId,
  onSelectRole,
  onAddRole,
  isLoading,
  roleUserCounts = {},
}) => {
  return (
    <Box data-testid="role-list-view">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
        data-testid="role-list-header"
      >
        {roles.length === 0 ? null : (<Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAddRole}
          size="large"
          data-testid="add-role-button"
        >
          Add Role
        </Button>)}
      </Box>

      {isLoading ? (
        <Card data-testid="role-list-loading">
          <CardContent>
            <Box display="flex" flexDirection="column" gap={1}>
              {[1, 2, 3, 4].map((i) => (
                <Box
                  key={i}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  py={1}
                  data-testid={`role-list-skeleton-${i}`}
                >
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width="30%" height={20} />
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      ) : roles.length === 0 ? (
        <Card data-testid="role-list-empty-state">
          <CardContent>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="200px"
              gap={2}
              data-testid="role-list-empty-content"
            >
              <PeopleIcon sx={{ fontSize: 64, color: "text.secondary" }} />
              <Typography variant="h6" color="text.secondary">
                No roles configured for this institute
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click Add Role to create one.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onAddRole}
                data-testid="add-role-empty-button"
              >
                Add Role
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="role-list-card">
          <List disablePadding>
            {roles.map((role) => (
              <ListItemButton
                key={role.id}
                selected={role.id === selectedRoleId}
                onClick={() => onSelectRole(role.id)}
                data-testid={`role-list-item-${role.id}`}
              >
                <ListItemText
                  primary={role.name}
                  secondary={role.description}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
                {role.id === selectedRoleId &&
                  roleUserCounts[role.id] != null && (
                    <Chip
                      label={`${roleUserCounts[role.id]} users`}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1 }}
                      data-testid={`role-user-count-${role.id}`}
                    />
                  )}
              </ListItemButton>
            ))}
          </List>
        </Card>
      )}
    </Box>
  );
};

export default RoleListView;
